import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { useIssueReportOffer } from '@/hooks/useIssueReportOffer';
import { ISSUE_REPORT_PATH_STRINGS as S, PURCHASE_INTENT } from '@/lib/constants/issue-report';
import { ROUTES } from '@/lib/constants/routes';
import { fillOfferTokens } from '@/lib/utils/offer-tokens.utils';
import { withPurchaseIntent } from '@/lib/utils/purchase-intent.utils';

interface IssueReportPathDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * What a signed-out visitor is about to do, shown before the Issue Report
 * journey starts (FR-223, E41): the four steps, each with what it involves,
 * then register or log in. Both links carry the intent, so the journey picks
 * up after either. The payment line takes its price from the public offer,
 * already cached by the CTA that opened this.
 */
export function IssueReportPathDialog({ open, onOpenChange }: IssueReportPathDialogProps) {
  const { data: offer } = useIssueReportOffer();

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={S.TITLE}
      description={S.DESCRIPTION}
      size="lg"
      align="center"
      headerIcon={<span className="text-green text-xs font-semibold tracking-widest uppercase">{S.EYEBROW}</span>}
      footer={
        <>
          <Button asChild variant="primary" className="inline-flex w-full justify-center">
            <Link to={withPurchaseIntent(ROUTES.AUTH.REGISTER, PURCHASE_INTENT.ISSUE_REPORT)}>{S.GET_STARTED}</Link>
          </Button>
          <Button asChild variant="outline" className="inline-flex w-full justify-center">
            <Link to={withPurchaseIntent(ROUTES.AUTH.LOGIN, PURCHASE_INTENT.ISSUE_REPORT)}>{S.HAVE_ACCOUNT}</Link>
          </Button>
        </>
      }
    >
      <ol className="flex flex-col gap-3">
        {S.STEPS.map((step, index) => (
          <li key={step.title} className="flex items-start gap-3.5">
            <span
              aria-hidden="true"
              className="bg-navy flex h-7.5 w-7.5 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white"
            >
              {index + 1}
            </span>
            <span className="flex flex-col gap-0.5">
              <strong className="text-navy text-[0.9375rem] font-semibold">{step.title}</strong>
              <span className="text-subtext text-[0.8125rem]">{fillOfferTokens(step.detail, offer)}</span>
            </span>
          </li>
        ))}
      </ol>
    </Dialog>
  );
}
